import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import ServiceForm from './service-form';
import ServiceCategoriesStep from './service-categories-step';
import ServiceOptionsStep from './service-options-step';
import { Service } from '../../model/professional/service-model';

interface ServiceStepperProps {
  mode: 'create' | 'edit';
  service?: Service;
  onComplete: (serviceId: string) => void;
  onCancel: () => void;
}

const ServiceStepper = observer((props: ServiceStepperProps) => {
  const { mode, service, onComplete, onCancel } = props;
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [createdServiceId, setCreatedServiceId] = useState<string | null>(service?.id || null);

  const steps = [
    { id: 'info', label: 'Informations', description: 'Détails du service' },
    { id: 'categories', label: 'Catégories', description: 'Options groupées (facultatif)' },
    { id: 'options', label: 'Options', description: 'Personnalisations (facultatif)' }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Dernière étape : terminer
      if (createdServiceId) {
        onComplete(createdServiceId);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleServiceCreated = (serviceId?: string | null) => {
    if (serviceId) {
      setCreatedServiceId(serviceId);
      // Passer automatiquement à l'étape suivante
      handleNext();
    }
  };

  const canGoNext = () => {
    // Étape 1 : doit avoir créé le service
    if (currentStep === 0) {
      return createdServiceId !== null;
    }
    // Autres étapes : toujours possible de continuer (facultatif)
    return true;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Indicateur de progression */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                    index < currentStep
                      ? 'bg-primary border-primary text-white'
                      : index === currentStep
                      ? 'border-primary text-primary bg-primary/10'
                      : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {index < currentStep ? (
                    <Check size={20} />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className={`text-sm font-medium ${
                      index <= currentStep
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-4 ${
                    index < currentStep
                      ? 'bg-primary'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Contenu de l'étape */}
      <div className="flex-1 overflow-hidden">
        {currentStep === 0 && (
          <ServiceForm
            mode={mode}
            service={service}
            onSuccess={handleServiceCreated}
          />
        )}
        {currentStep === 1 && createdServiceId && (
          <ServiceCategoriesStep serviceId={createdServiceId} />
        )}
        {currentStep === 2 && createdServiceId && (
          <ServiceOptionsStep serviceId={createdServiceId} />
        )}
      </div>

      {/* Navigation */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex justify-between items-center">
          <div>
            {currentStep > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="inline-flex items-center"
              >
                <ChevronLeft size={16} className="mr-1" />
                Précédent
              </Button>
            )}
          </div>
          <div className="flex space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext()}
              className="inline-flex items-center"
            >
              {currentStep === steps.length - 1 ? (
                'Terminer'
              ) : (
                <>
                  Suivant
                  <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ServiceStepper;
